import { extendTheme } from '@mui/joy/styles';

// ── Light palette ─────────────────────────────────────────────────────────────
// Primary: steel blue  |  Success: green  |  Warning: amber  |  Danger: red
// Info: sky blue  |  Neutral: standard grey
const lightPalette = {
  primary: {
    50:  '#E3F2FD',
    100: '#BBDEFB',
    200: '#90CAF9',
    300: '#64B5F6',
    400: '#42A5F5',
    500: '#2196F3',
    600: '#1E88E5',
    700: '#1976D2',
    800: '#1565C0',
    900: '#0D47A1',
    solidBg:        'var(--joy-palette-primary-600)',
    solidHoverBg:   'var(--joy-palette-primary-700)',
    solidActiveBg:  'var(--joy-palette-primary-800)',
    solidColor:     '#FFFFFF',
    softColor:      'var(--joy-palette-primary-700)',
    softBg:         'var(--joy-palette-primary-100)',
    softHoverBg:    'var(--joy-palette-primary-200)',
    softActiveBg:   'var(--joy-palette-primary-300)',
    outlinedColor:  'var(--joy-palette-primary-700)',
    outlinedBorder: 'var(--joy-palette-primary-400)',
    outlinedHoverBg:'var(--joy-palette-primary-50)',
    outlinedActiveBg:'var(--joy-palette-primary-100)',
    plainColor:     'var(--joy-palette-primary-700)',
    plainHoverBg:   'var(--joy-palette-primary-50)',
    plainActiveBg:  'var(--joy-palette-primary-100)',
  },
  success: {
    50:  '#E8F5E9',
    100: '#C8E6C9',
    200: '#A5D6A7',
    300: '#81C784',
    400: '#66BB6A',
    500: '#4CAF50',
    600: '#43A047',
    700: '#388E3C',
    800: '#2E7D32',
    900: '#1B5E20',
    solidBg:       'var(--joy-palette-success-600)',
    solidHoverBg:  'var(--joy-palette-success-700)',
    solidActiveBg: 'var(--joy-palette-success-800)',
    solidColor:    '#FFFFFF',
  },
  warning: {
    50:  '#FFF8E1',
    100: '#FFECB3',
    200: '#FFE082',
    300: '#FFD54F',
    400: '#FFCA28',
    500: '#FFC107',
    600: '#FFB300',
    700: '#FFA000',
    800: '#FF8F00',
    900: '#FF6F00',
    solidBg:       'var(--joy-palette-warning-600)',
    solidHoverBg:  'var(--joy-palette-warning-700)',
    solidActiveBg: 'var(--joy-palette-warning-800)',
    solidColor:    '#212121',
  },
  danger: {
    50:  '#FFEBEE',
    100: '#FFCDD2',
    200: '#EF9A9A',
    300: '#E57373',
    400: '#EF5350',
    500: '#F44336',
    600: '#E53935',
    700: '#D32F2F',
    800: '#C62828',
    900: '#B71C1C',
    solidBg:       'var(--joy-palette-danger-600)',
    solidHoverBg:  'var(--joy-palette-danger-700)',
    solidActiveBg: 'var(--joy-palette-danger-800)',
    solidColor:    '#FFFFFF',
  },
  neutral: {
    50:  '#FAFAFA',
    100: '#F5F5F5',
    200: '#EEEEEE',
    300: '#E0E0E0',
    400: '#BDBDBD',
    500: '#9E9E9E',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },
  info: {
    50:  '#E1F5FE',
    100: '#B3E5FC',
    200: '#81D4FA',
    300: '#4FC3F7',
    400: '#29B6F6',
    500: '#03A9F4',
    600: '#039BE5',
    700: '#0288D1',
    800: '#0277BD',
    900: '#01579B',
    solidBg:       'var(--joy-palette-info-600)',
    solidHoverBg:  'var(--joy-palette-info-700)',
    solidActiveBg: 'var(--joy-palette-info-800)',
    solidColor:    '#FFFFFF',
    softColor:     'var(--joy-palette-info-700)',
    softBg:        'var(--joy-palette-info-100)',
    outlinedColor: 'var(--joy-palette-info-700)',
    outlinedBorder:'var(--joy-palette-info-400)',
    plainColor:    'var(--joy-palette-info-700)',
  },
  background: {
    body:    '#F5F5F5',
    surface: '#FFFFFF',
    level1:  '#F0F4F8',
    level2:  '#E3E8EF',
    level3:  '#D3DCE6',
  },
  text: {
    primary:   '#212121',
    secondary:  '#616161',
    tertiary:  '#9E9E9E',
    icon:      '#757575',
  },
  divider: 'rgba(0,0,0,0.12)',
};

// ── Dark palette ──────────────────────────────────────────────────────────────
// Background: near-black / dark grey  |  Primary: gold  |  Success: red
// Warning: amber  |  Danger: deep crimson  |  Info: steel blue
// Accent white used for text and solid button labels
const darkPalette = {
  primary: {
    // Gold — main interactive colour (goldenrod-based, less saturated than pure yellow)
    50:  '#FEFBE8',
    100: '#FDF3C0',
    200: '#FAE47A',
    300: '#F0C93A',
    400: '#E0AE1A',
    500: '#DAA520',
    600: '#C08010',
    700: '#A06800',
    800: '#7A5000',
    900: '#543800',
    solidBg:         'var(--joy-palette-primary-500)',
    solidHoverBg:    'var(--joy-palette-primary-600)',
    solidActiveBg:   'var(--joy-palette-primary-700)',
    solidColor:      '#0A0A0A',
    softColor:       'var(--joy-palette-primary-300)',
    softBg:          'rgba(218,165,32,0.18)',
    softHoverBg:     'rgba(218,165,32,0.28)',
    softActiveBg:    'rgba(218,165,32,0.38)',
    outlinedColor:   'var(--joy-palette-primary-300)',
    outlinedBorder:  'var(--joy-palette-primary-600)',
    outlinedHoverBg: 'rgba(218,165,32,0.10)',
    outlinedActiveBg:'rgba(218,165,32,0.20)',
    plainColor:      'var(--joy-palette-primary-300)',
    plainHoverBg:    'rgba(218,165,32,0.10)',
    plainActiveBg:   'rgba(218,165,32,0.20)',
  },
  success: {
    // Vivid red — positive/success states
    50:  '#FFEBEE',
    100: '#FFCDD2',
    200: '#EF9A9A',
    300: '#E57373',
    400: '#EF5350',
    500: '#F44336',
    600: '#E53935',
    700: '#D32F2F',
    800: '#C62828',
    900: '#B71C1C',
    solidBg:         'var(--joy-palette-success-600)',
    solidHoverBg:    'var(--joy-palette-success-500)',
    solidActiveBg:   'var(--joy-palette-success-700)',
    solidColor:      '#FAFAFA',
    softColor:       'var(--joy-palette-success-300)',
    softBg:          'rgba(244,67,54,0.14)',
    softHoverBg:     'rgba(244,67,54,0.24)',
    outlinedColor:   'var(--joy-palette-success-300)',
    outlinedBorder:  'var(--joy-palette-success-700)',
    outlinedHoverBg: 'rgba(244,67,54,0.10)',
    plainColor:      'var(--joy-palette-success-300)',
    plainHoverBg:    'rgba(244,67,54,0.10)',
  },
  warning: {
    // Amber — distinct from gold, used for cautions
    50:  '#FFF8E1',
    100: '#FFECB3',
    200: '#FFE082',
    300: '#FFD54F',
    400: '#FFCA28',
    500: '#FFC107',
    600: '#FFB300',
    700: '#FFA000',
    800: '#FF8F00',
    900: '#FF6F00',
    solidBg:       'var(--joy-palette-warning-600)',
    solidHoverBg:  'var(--joy-palette-warning-700)',
    solidActiveBg: 'var(--joy-palette-warning-800)',
    solidColor:    '#0A0A0A',
    softColor:     'var(--joy-palette-warning-300)',
    softBg:        'rgba(255,193,7,0.14)',
    outlinedColor: 'var(--joy-palette-warning-300)',
    outlinedBorder:'var(--joy-palette-warning-700)',
    plainColor:    'var(--joy-palette-warning-300)',
  },
  danger: {
    // Deep crimson — destructive actions, clearly distinct from primary red
    50:  '#FFEBEE',
    100: '#FFCDD2',
    200: '#EF9A9A',
    300: '#EF5350',
    400: '#E53935',
    500: '#C62828',
    600: '#B71C1C',
    700: '#9B1515',
    800: '#7D0000',
    900: '#5D0000',
    solidBg:         'var(--joy-palette-danger-500)',
    solidHoverBg:    'var(--joy-palette-danger-600)',
    solidActiveBg:   'var(--joy-palette-danger-700)',
    solidColor:      '#FAFAFA',
    softColor:       'var(--joy-palette-danger-300)',
    softBg:          'rgba(198,40,40,0.18)',
    softHoverBg:     'rgba(198,40,40,0.28)',
    outlinedColor:   'var(--joy-palette-danger-300)',
    outlinedBorder:  'var(--joy-palette-danger-600)',
    outlinedHoverBg: 'rgba(198,40,40,0.12)',
    plainColor:      'var(--joy-palette-danger-300)',
    plainHoverBg:    'rgba(198,40,40,0.10)',
  },
  neutral: {
    // Dark greys — surfaces, borders, muted elements
    50:  '#FAFAFA',
    100: '#F0F0F0',
    200: '#D6D6D6',
    300: '#ADADAD',
    400: '#858585',
    500: '#5E5E5E',
    600: '#3D3D3D',
    700: '#2A2A2A',
    800: '#1A1A1A',
    900: '#0F0F0F',
    solidBg:         'var(--joy-palette-neutral-700)',
    solidHoverBg:    'var(--joy-palette-neutral-600)',
    solidActiveBg:   'var(--joy-palette-neutral-800)',
    solidColor:      '#FAFAFA',
    softColor:       'var(--joy-palette-neutral-200)',
    softBg:          'var(--joy-palette-neutral-800)',
    softHoverBg:     'var(--joy-palette-neutral-700)',
    softActiveBg:    'var(--joy-palette-neutral-600)',
    outlinedColor:   'var(--joy-palette-neutral-200)',
    outlinedBorder:  'var(--joy-palette-neutral-600)',
    outlinedHoverBg: 'rgba(255,255,255,0.06)',
    outlinedActiveBg:'rgba(255,255,255,0.12)',
    plainColor:      'var(--joy-palette-neutral-200)',
    plainHoverBg:    'rgba(255,255,255,0.06)',
    plainActiveBg:   'rgba(255,255,255,0.12)',
  },
  info: {
    // Steel blue — informational alerts, visually distinct from red and gold
    50:  '#ECEFF1',
    100: '#CFD8DC',
    200: '#B0BEC5',
    300: '#90A4AE',
    400: '#78909C',
    500: '#607D8B',
    600: '#546E7A',
    700: '#455A64',
    800: '#37474F',
    900: '#263238',
    solidBg:       'var(--joy-palette-info-600)',
    solidHoverBg:  'var(--joy-palette-info-500)',
    solidActiveBg: 'var(--joy-palette-info-700)',
    solidColor:    '#FAFAFA',
    softColor:     'var(--joy-palette-info-200)',
    softBg:        'rgba(96,125,139,0.18)',
    softHoverBg:   'rgba(96,125,139,0.28)',
    outlinedColor: 'var(--joy-palette-info-200)',
    outlinedBorder:'var(--joy-palette-info-600)',
    outlinedHoverBg:'rgba(96,125,139,0.12)',
    plainColor:    'var(--joy-palette-info-200)',
    plainHoverBg:  'rgba(96,125,139,0.10)',
  },
  background: {
    body:    '#0A0A0A',
    surface: '#141414',
    level1:  '#1E1E1E',
    level2:  '#282828',
    level3:  '#323232',
    tooltip: '#1E1E1E',
    popup:   '#141414',
  },
  text: {
    primary:   '#FAFAFA',
    secondary:  '#BDBDBD',
    tertiary:  '#757575',
    icon:      '#9E9E9E',
  },
  divider: 'rgba(255,255,255,0.12)',
  focusVisible: 'rgba(244,67,54,0.6)',
};

const theme = extendTheme({
  colorSchemes: {
    light: { palette: lightPalette },
    dark:  { palette: darkPalette  },
  },
});

export default theme;
